/**
 *
 * MobileLayout
 *
 */

import React, { memo, useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import classNames from "classnames";
import { useLocation, withRouter } from "react-router-dom";
// import queryString from 'query-string';

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

// styles
import "./style.scss";

import { flushState } from "./actions";
import {
  makeSelectError,
  makeSelectLoading,
  makeSelectMobileLayout,
} from "./selectors";

// antd component
import { Layout } from "antd";
const { Content } = Layout;
import { SafeArea } from "antd-mobile";

// assets
//import CustomIcons from 'components/CustomIcons';

// layout components
import Preloader from "components/Preloader";
import MobileHeaderLayout from "layouts/MobileHeaderLayout";
import MobileBottomBar from "layouts/MobileBottomBar";
// parent
import MobileParentHeader from "layouts/MobileParentHeader";
import MobileTeacherHeader from "layouts/MobileTeacherHeader";

// utils
import { appHelper } from "appCapacitor/helpers";
import useDeviceDetect from "utils/useDeviceDetect";
import AuthRoute from "engine/AuthRoute";

// context
import { useLogout } from "engine/Contexts/User.context";

// Mobile Pages
import AbsenceMobile from "mobile/pages/AbsenceMobile";
import Home from "mobile/pages/Home";
import CoursesMobile from "mobile/pages/CoursesMobile";
import Attendance from "mobile/pages/Attendance";
import CourseDetailMobile from "mobile/pages/CourseDetailMobile";
// parent & teacher
import CommunicationsMobile from "mobile/pages/CommunicationsMobile";
// parent
import CommunicationDetailMobile from "mobile/pages/CommunicationDetailMobile";
import CommunicationNewMobile from "mobile/pages/CommunicationNewMobile";
import SignupParent from "mobile/pages/SignupParent";
// teacher
import TeacherCommunicationDetailMobile from "mobile/pages/TeacherCommunicationDetailMobile";

// utils
import _local from "utils/localStorageUtils";

export function MobileLayout({
  // actions here
  history,
  flushState,
  // default props
  className,
}) {
  useInjectReducer({ key: "mobileLayout", reducer });
  useInjectSaga({ key: "mobileLayout", saga });

  const location = useLocation();
  const { isMobile } = useDeviceDetect();

  const mobileMainRef = useRef();

  const currentPath = location.pathname.replace(/\//g, "");
  const defaultPath = currentPath ? currentPath : "";
  // const params = queryString.parse(history.location.search);

  let isApp = false;
  if (!appHelper.isWeb || isMobile) {
    isApp = true;
  }

  const logOut = useLogout();
  const localUser = _local.getLocalUser();

  const [activeMenu, setActiveMenu] = useState("/" + defaultPath);
  const [spinner, setSpinner] = useState(true);

  const [collapseHeader, setCollapseHeader] = useState(false);

  const [notifications, setNotifications] = useState([]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    const preloader = setTimeout(() => setSpinner(false), 1500);

    // Anything in here is fired on component unmount.
    return () => {
      // this will clear setTimeout
      clearTimeout(preloader);
      setCollapseHeader(false);
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  // Log out from mobile version if user role is not 'teacher' or 'parent'
  useEffect(() => {
    if (isApp && localUser && localUser?.role == "admin") {
      logOut();
    }
  }, [isApp]);

  const changeTabNavigation = route => {
    setActiveMenu(route);
    history.push(`${route}`);
  };

  // Hide/Show Second Header for Parent/Teacher
  const handleMobileLayoutScroll = () => {
    const scrollTop = mobileMainRef?.current?.scrollTop;
    if (scrollTop <= 5) {
      setCollapseHeader(false);
    } else {
      setCollapseHeader(true);
    }
  };

  // TODO: manage code below on another way, add additional hook or similar, 
  // INFO: moved this logic from parent and teacher headers to this parent page
  // hide headers component for path from this array - hideForPath
  const isPublicPath = useCallback(() => {
    const pathname = location?.pathname;
    const isPath = [
      "/signupparent",
      "/reset",
      "/confirm",
      "/linkstudent",
      "/login",
    ]?.some(p => pathname.includes(p));
    return !!isPath;
  }, [location?.pathname]);

  // render function
  return (
    <>
      {spinner && <Preloader fullWidth />}
      {!spinner && (
        <>
          <div
            style={{
              background: localUser?.role == "teacher" ? "#f59c05" : "#005D9F",
            }}
          >
            <SafeArea position="top" />
          </div>
          <Layout className="app-main-layout app-main-layout--mobile">
            {/* Mobile Header/SideBar Menu */}
            <MobileHeaderLayout />

            <Content
              className={classNames(
                collapseHeader && "collapsed-header",
                `layout-content mobile-layout-content ${
                  !!appHelper?.isIos ? "ios-mobile-layout" : " "
                } ${currentPath}`,
              )}
              ref={mobileMainRef}
              onScroll={handleMobileLayoutScroll}
            >
              <MobileParentHeader
                roles={["parent"]}
                visibility={!isPublicPath()}
                notifications={notifications}
                collapse={collapseHeader}
              />

              <MobileTeacherHeader
                roles={["teacher"]}
                visibility={!isPublicPath()}
                notifications={notifications}
                collapse={collapseHeader}
              />

              {/* News */}
              <AuthRoute
                path={["/", "/home", "/communications"]}
                component={CommunicationsMobile}
                roles={["parent", "teacher"]}
                exact
              />
              <AuthRoute
                path={"/communications/:ID"}
                component={CommunicationDetailMobile}
                roles={["parent"]}
                exact
              />
              <AuthRoute
                path={"/communications/new"}
                component={CommunicationNewMobile}
                roles={["parent"]}
                exact
              />
              <AuthRoute
                path={"/communications/:ID"}
                component={TeacherCommunicationDetailMobile}
                roles={["teacher"]}
                exact
              />

              {/* Mobile Pages */}
              <AuthRoute
                path={["/linkstudent"]}
                component={SignupParent}
                roles={["parent"]}
              />

              <AuthRoute
                path="/absences"
                component={AbsenceMobile}
                roles={["parent", "teacher"]}
                exact
              />
              <AuthRoute
                path="/absences/:id"
                component={null}
                roles={["parent", "teacher"]}
              />
              <AuthRoute
                path="/attendance"
                component={Attendance}
                roles={["teacher"]}
                exact
              />
              <AuthRoute
                path={"/сours"}
                component={CoursesMobile}
                roles={["parent", "teacher"]}
                exact
              />
              <AuthRoute
                path={"/сours/:id"}
                component={CourseDetailMobile}
                roles={["teacher"]}
                exact
              />
            </Content>
          </Layout>

          {/* Mobile Bottom Bar */}
          <MobileBottomBar
            selectedTab={activeMenu}
            changeTab={changeTabNavigation}
            notifications={notifications}
          />
          <div
            style={{
              background: "#fff",
              overflowY: "hidden",
            }}
          >
            <SafeArea position="bottom" />
          </div>
        </>
      )}
    </>
  );
}

MobileLayout.defaultProps = {};

MobileLayout.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  mobileLayout: makeSelectMobileLayout(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withRouter,
)(MobileLayout);
